import { useEffect, useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { Loader2, Save, X } from "lucide-react";

const initialForm = {
  dailyTaskLimit: "",
  concurrentOrder: "",
  maxTeamLimit: "",
  cashLimit: "",
};

export default function PackerTaskConfigModal({
  open,
  onOpenChange,
  drivers = [],
  getPackerTaskConfig,
  savePackerTaskConfig,
  onSuccess,
}) {
  const [driverId, setDriverId] = useState("");
  const [form, setForm] = useState(initialForm);
  const [existingConfig, setExistingConfig] = useState(null);
  const [fetching, setFetching] = useState(false);
  const [saving, setSaving] = useState(false);

  const resetModal = () => {
    setDriverId("");
    setForm(initialForm);
    setExistingConfig(null);
    setFetching(false);
    setSaving(false);
  };

  useEffect(() => {
    if (!open) {
      resetModal();
    }
  }, [open]);

  const handleDriverChange = async (event) => {
    const selectedDriverId = event.target.value;

    setDriverId(selectedDriverId);
    setExistingConfig(null);
    setForm(initialForm);

    if (!selectedDriverId) return;

    try {
      setFetching(true);

      // FlagId 2 call
      const config = await getPackerTaskConfig(selectedDriverId);

      if (config) {
        setExistingConfig(config);

        setForm({
          dailyTaskLimit: String(config?.daily_task_limit ?? ""),
          concurrentOrder: String(
            config?.concurrent_orders ?? config?.concurrent_order ?? "",
          ),
          maxTeamLimit: String(config?.max_team_limit ?? ""),
          cashLimit: String(config?.cash_limit ?? ""),
        });
      }
    } finally {
      setFetching(false);
    }
  };

  const handleChange = (event) => {
    const { name, value } = event.target;

    if (value !== "" && Number(value) < 0) {
      return;
    }

    setForm((previous) => ({
      ...previous,
      [name]: value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    try {
      setSaving(true);

      const payload = {
        driverId,
        dailyTaskLimit: form.dailyTaskLimit,
        concurrentOrder: form.concurrentOrder,
        maxTeamLimit: form.maxTeamLimit,
        cashLimit: form.cashLimit,
      };

      const isEdit = Boolean(existingConfig);

      // Existing config hai to FlagId 3
      // Existing config nahi hai to FlagId 1
      const success = await savePackerTaskConfig(payload, isEdit);

      if (success) {
        onSuccess?.();
        onOpenChange(false);
        resetModal();
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/40" />

        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-[94%] max-w-lg -translate-x-1/2 -translate-y-1/2 overflow-hidden rounded-2xl bg-white shadow-xl">
          <div className="flex items-center justify-between border-b bg-orange-50 px-5 py-4">
            <div>
              <Dialog.Title className="text-base font-semibold text-gray-900">
                Packer Task Configuration
              </Dialog.Title>

              <p className="mt-1 text-xs text-gray-500">
                {existingConfig
                  ? "Existing configuration will be updated"
                  : "Select a driver to add configuration"}
              </p>
            </div>

            <Dialog.Close asChild>
              <button
                type="button"
                className="rounded-lg p-2 text-gray-500 hover:bg-white"
                disabled={saving}
              >
                <X size={18} />
              </button>
            </Dialog.Close>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="space-y-4 px-5 py-5">
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-700">
                  Driver
                </label>

                <select
                  value={driverId}
                  onChange={handleDriverChange}
                  disabled={saving}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-orange-500"
                >
                  <option value="">Select driver</option>

                  {drivers.map((driver) => {
                    const code = driver?.driverId || driver?.driver_code || "";

                    const name =
                      driver?.fullName ||
                      driver?.name ||
                      driver?.username ||
                      code;

                    return (
                      <option key={code} value={code}>
                        {name} ({code})
                      </option>
                    );
                  })}
                </select>
              </div>

              {fetching ? (
                <div className="flex items-center justify-center gap-2 rounded-xl border bg-gray-50 py-8 text-sm text-gray-600">
                  <Loader2 size={18} className="animate-spin" />
                  Fetching configuration...
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label className="mb-1 block text-xs font-medium text-gray-700">
                      Daily Task Limit
                    </label>

                    <input
                      type="number"
                      min="0"
                      name="dailyTaskLimit"
                      value={form.dailyTaskLimit}
                      onChange={handleChange}
                      disabled={!driverId || saving}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-orange-500 disabled:bg-gray-100"
                    />
                  </div>

                  <div>
                    <label className="mb-1 block text-xs font-medium text-gray-700">
                      Concurrent Orders
                    </label>

                    <input
                      type="number"
                      min="0"
                      name="concurrentOrder"
                      value={form.concurrentOrder}
                      onChange={handleChange}
                      disabled={!driverId || saving}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-orange-500 disabled:bg-gray-100"
                    />
                  </div>

                  <div>
                    <label className="mb-1 block text-xs font-medium text-gray-700">
                      Maximum Team Limit
                    </label>

                    <input
                      type="number"
                      min="0"
                      name="maxTeamLimit"
                      value={form.maxTeamLimit}
                      onChange={handleChange}
                      disabled={!driverId || saving}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-orange-500 disabled:bg-gray-100"
                    />
                  </div>

                  <div>
                    <label className="mb-1 block text-xs font-medium text-gray-700">
                      Cash Limit
                    </label>

                    <input
                      type="number"
                      min="0"
                      name="cashLimit"
                      value={form.cashLimit}
                      onChange={handleChange}
                      disabled={!driverId || saving}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-orange-500 disabled:bg-gray-100"
                    />
                  </div>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-2 border-t px-5 py-4">
              <Dialog.Close asChild>
                <button
                  type="button"
                  disabled={saving}
                  className="rounded-lg border px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-60"
                >
                  Cancel
                </button>
              </Dialog.Close>

              <button
                type="submit"
                disabled={!driverId || fetching || saving}
                className="inline-flex items-center gap-2 rounded-lg bg-orange-500 px-4 py-2 text-sm font-medium text-white hover:bg-orange-600 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {saving ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : (
                  <Save size={16} />
                )}

                {saving
                  ? "Saving..."
                  : existingConfig
                    ? "Update Config"
                    : "Add Config"}
              </button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
